import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Bot, Send, Plus, Loader2, User, CalendarCheck, CalendarRange, Inbox, Check, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';

const SUGGESTIONS = [
  'Wat zijn de kosten en inkopen van deze maand?',
  'Welke inkoopfacturen moeten nog betaald worden?',
  'Hoeveel uren hebben de medewerkers deze week gemaakt?',
  'Welke offertes wachten nog op akkoord?',
];

const DAY_PROMPT = 'Geef mij een dagoverzicht van vandaag: kosten & inkoop, uren, offertes, facturen, agenda en aandachtspunten.';
const WEEK_PROMPT = 'Geef mij een weekoverzicht: kosten en inkoop deze maand, uren per medewerker deze week, openstaande offertes en facturen, agenda komende week en aandachtspunten.';
const MAIL_PROMPT = 'Haal de facturen uit mijn e-mail en verwerk ze op de juiste plek.';

const TOOL_LABELS = {
  zoek_offertes: 'Offertes doorzocht', zoek_facturen: 'Facturen doorzocht', zoek_kosten: 'Kosten & inkoop doorzocht',
  zoek_uren: 'Uren doorzocht', klant_dossier: 'Klantdossier geopend', agenda: 'Agenda bekeken',
  email_import_status: 'Importstatus bekeken', start_email_import: 'E-mail import gestart',
};

const parseAction = (content) => {
  let text = (content || '').replaceAll('[[IMPORT_GESTART]]', '');
  const tools = [];
  text = text.replace(/```tool\s*([\s\S]*?)```/g, (_, body) => {
    try { tools.push(JSON.parse(body).name || 'opzoeken'); } catch { tools.push('opzoeken'); }
    return '';
  });
  text = text.replace(/```tool[\s\S]*$/, '');
  const m = text.match(/```action\s*([\s\S]*?)```/);
  if (!m) return { text: text.trim(), action: null, tools };
  try {
    return { text: text.replace(m[0], '').trim(), action: JSON.parse(m[1]), tools };
  } catch {
    return { text: text.trim(), action: null, tools };
  }
};

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('assistant_session_id') || crypto.randomUUID());
  const [actionStates, setActionStates] = useState({});
  const [importProgress, setImportProgress] = useState(null);
  const importPollRef = useRef(null);
  const bottomRef = useRef(null);

  const startImportPolling = () => {
    if (importPollRef.current) return;
    setImportProgress({ running: true, processed: 0, total: 0 });
    importPollRef.current = setInterval(async () => {
      try {
        const r = await axiosInstance.get('/email-import/progress');
        const d = r.data || {};
        setImportProgress(d);
        if (!d.running) {
          clearInterval(importPollRef.current);
          importPollRef.current = null;
          setImportProgress(null);
          const res = d.result || {};
          const errs = (res.accounts || []).filter((a) => a.error).map((a) => `${a.account}: ${a.error}`);
          let txt = `✅ **E-mail import afgerond** — ${res.processed || 0} document(en) verwerkt en in **Uitgaven** geplaatst`;
          if (res.inkoop_count) txt += `\n- 📥 ${res.inkoop_count} naar Inkoopfacturen`;
          if (res.leads_found) txt += `\n- ✉️ ${res.leads_found} nieuwe aanvra(a)g(en)`;
          if (res.processed) txt += '\n\nWil je een item toch bij Inkoopfacturen hebben? Op de Uitgaven-pagina verplaats je het met één klik (⇄).';
          if (!res.processed) txt += '\n\nGeen nieuwe facturen gevonden — alles was al geïmporteerd of er zaten geen factuur-PDF\'s in de mail.';
          if (errs.length) txt += `\n\n⚠️ Problemen: ${errs.join('; ')}`;
          setMessages((prev) => [...prev, { role: 'assistant', content: txt }]);
        }
      } catch {}
    }, 2500);
  };

  useEffect(() => () => { if (importPollRef.current) clearInterval(importPollRef.current); }, []);

  const executeAction = async (idx, action) => {
    setActionStates((s) => ({ ...s, [idx]: 'busy' }));
    try {
      const r = await axiosInstance.post('/assistant/execute-action', { action, session_id: sessionId });
      setActionStates((s) => ({ ...s, [idx]: 'done' }));
      setMessages((prev) => [...prev, { role: 'assistant', content: `✅ Uitgevoerd: ${r.data.message}` }]);
      toast.success(r.data.message);
    } catch (e) {
      setActionStates((s) => ({ ...s, [idx]: 'pending' }));
      toast.error(e.response?.data?.detail || 'Actie mislukt');
    }
  };

  useEffect(() => {
    localStorage.setItem('assistant_session_id', sessionId);
    axiosInstance.get(`/assistant/history?session_id=${sessionId}`)
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const newConversation = () => {
    const id = crypto.randomUUID();
    setMessages([]);
    setActionStates({});
    setSessionId(id);
  };

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput('');
    setBusy(true);
    setMessages((prev) => [...prev, { role: 'user', content: msg }, { role: 'assistant', content: '' }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      if (!res.ok || !res.body) throw new Error('request failed');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        if (acc.includes('[[IMPORT_GESTART]]')) startImportPolling();
        const displayed = acc.replaceAll('[[IMPORT_GESTART]]', '');
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: displayed };
          return copy;
        });
      }
    } catch {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: 'assistant', content: 'Er ging iets mis. Probeer het opnieuw.' };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  // Auto-verstuur prompt uit URL (bijv. vanaf de Aanvragen-pagina)
  const autoSentRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const p = searchParams.get('prompt');
    if (p && !autoSentRef.current) {
      autoSentRef.current = true;
      setSearchParams({}, { replace: true });
      send(p);
    }
  });

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] max-w-4xl mx-auto" data-testid="assistant-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Bot className="w-6 h-6 text-[#1a5276]" /> Assistent
          </h1>
          <p className="text-gray-500 text-sm">Vraag alles over uren, kosten, offertes, facturen, klanten, agenda en e-mails</p>
        </div>
        <Button variant="outline" size="sm" onClick={newConversation} data-testid="assistant-new-conversation">
          <Plus className="w-4 h-4 mr-1" /> Nieuw gesprek
        </Button>
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        <Button size="sm" onClick={() => send(DAY_PROMPT)} disabled={busy} className="bg-[#1a5276] hover:bg-[#154360]" data-testid="assistant-day-summary">
          <CalendarCheck className="w-4 h-4 mr-1" /> Dagoverzicht
        </Button>
        <Button size="sm" onClick={() => send(WEEK_PROMPT)} disabled={busy} className="bg-[#1a5276] hover:bg-[#154360]" data-testid="assistant-week-summary">
          <CalendarRange className="w-4 h-4 mr-1" /> Weekoverzicht
        </Button>
        <Button size="sm" onClick={() => send(MAIL_PROMPT)} disabled={busy} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="assistant-mail-import">
          <Inbox className="w-4 h-4 mr-1" /> Facturen uit e-mail halen
        </Button>
      </div>

      <Card className="flex-1 bg-white shadow-sm border-gray-200 overflow-hidden flex flex-col">
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="assistant-messages">
          {messages.length === 0 && (
            <div className="text-center py-10">
              <Bot className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">Stel een vraag of kies een voorbeeld:</p>
              <div className="flex flex-col items-center gap-2">
                {SUGGESTIONS.map((sug, i) => (
                  <button
                    key={i}
                    onClick={() => send(sug)}
                    className="text-sm text-[#1a5276] bg-[#1a5276]/5 hover:bg-[#1a5276]/10 rounded-full px-4 py-1.5 transition-colors"
                    data-testid={`assistant-suggestion-${i}`}
                  >
                    {sug}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => {
            const parsed = m.role === 'assistant' ? parseAction(m.content) : { text: m.content, action: null };
            return (
            <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-[#1a5276] flex items-center justify-center shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
              )}
              <div className="max-w-[80%] flex flex-col gap-2">
                {m.role === 'assistant' && parsed.tools && parsed.tools.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {parsed.tools.map((t, ti) => (
                      <span key={ti} className="text-[10px] bg-gray-100 border border-gray-200 text-gray-500 rounded-full px-2 py-0.5" data-testid={`assistant-tool-chip-${i}-${ti}`}>
                        🔎 {TOOL_LABELS[t] || t}
                      </span>
                    ))}
                  </div>
                )}
                <div className={`rounded-2xl px-4 py-2.5 text-sm ${m.role === 'user' ? 'bg-[#1a5276] text-white whitespace-pre-wrap' : 'bg-gray-100 text-gray-800 assistant-md'}`}>
                  {m.role === 'assistant' ? (
                    parsed.text || parsed.action ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{parsed.text || '_Voorstel:_'}</ReactMarkdown>
                    ) : (busy && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin" /> : '')
                  ) : m.content}
                </div>
                {parsed.action && actionStates[i] !== 'done' && actionStates[i] !== 'cancelled' && (
                  <div className="border-2 border-[#1a5276]/30 bg-[#1a5276]/5 rounded-xl p-3" data-testid={`assistant-action-card-${i}`}>
                    <p className="text-xs font-bold text-[#1a5276] mb-1">🔧 Voorgestelde actie</p>
                    <p className="text-sm text-gray-700 mb-2">{parsed.action.samenvatting || parsed.action.type}</p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => executeAction(i, parsed.action)} disabled={actionStates[i] === 'busy'} className="bg-green-600 hover:bg-green-700" data-testid={`assistant-action-confirm-${i}`}>
                        {actionStates[i] === 'busy' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Bevestigen</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setActionStates((s) => ({ ...s, [i]: 'cancelled' }))} data-testid={`assistant-action-cancel-${i}`}>
                        <X className="w-4 h-4 mr-1" /> Annuleren
                      </Button>
                    </div>
                  </div>
                )}
                {parsed.action && actionStates[i] === 'cancelled' && (
                  <p className="text-xs text-gray-400 italic">Actie geannuleerd</p>
                )}
              </div>
              {m.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
              )}
            </div>
            );
          })}
          {importProgress?.running && (
            <div className="flex gap-2 justify-start" data-testid="assistant-import-progress">
              <div className="w-8 h-8 rounded-full bg-[#3da5a5] flex items-center justify-center shrink-0">
                <Inbox className="w-4 h-4 text-white" />
              </div>
              <div className="bg-[#3da5a5]/10 border border-[#3da5a5]/30 rounded-2xl px-4 py-3 text-sm min-w-[260px]">
                <p className="font-semibold text-[#2d8585] flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> E-mail import bezig...
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {importProgress.total > 0
                    ? `${importProgress.processed || 0} van ${importProgress.total} facturen verwerkt`
                    : 'Mailbox doorzoeken...'}
                  {importProgress.account ? ` · ${importProgress.account}` : ''}
                </p>
                <div className="mt-2 h-1.5 bg-white rounded-full overflow-hidden">
                  <div className="h-full bg-[#3da5a5] transition-all duration-500"
                    style={{ width: importProgress.total > 0 ? `${Math.min(100, ((importProgress.processed || 0) / importProgress.total) * 100)}%` : '10%' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </CardContent>
        <div className="border-t border-gray-200 p-3 flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Typ je vraag..."
            rows={1}
            className="flex-1 resize-none rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a5276]/30"
            data-testid="assistant-input"
          />
          <Button onClick={() => send()} disabled={busy || !input.trim()} className="bg-[#1a5276] hover:bg-[#154360]" data-testid="assistant-send-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </Card>
    </div>
  );
}
